import { useCallback, useEffect, useRef, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import {
  FormFieldValue,
  FormTreeData,
  QuestionData,
} from '@/features/submission_forms/types';
import { buildQuestionData } from './useFormTree';
import { buildVisibilityMap, isAnsweredValue } from './questionVisibility';

const sameValue = (a: FormFieldValue, b: FormFieldValue): boolean =>
  Array.isArray(a) && Array.isArray(b)
    ? a.length === b.length && a.every((v, i) => v === b[i])
    : a === b;

export interface PendingBranchClear {
  /** Slug of the gating question the user just changed */
  slug: string;
  /** The value the user tried to set */
  nextValue: FormFieldValue;
  /** Answered questions that would be hidden (and cleared) by this change */
  affectedQuestions: QuestionData[];
}

interface UseBranchClearGuardOptions {
  form: UseFormReturn;
  formTree: FormTreeData[];
  enabled?: boolean;
}

/**
 * Warns before a change to a gating answer silently wipes previously-answered
 * branch questions.
 *
 * Subscribes to form changes; when a change would flip any *answered* question
 * from visible to hidden (transitively), the change is reverted and surfaced as
 * `pending` so the caller can render a confirmation dialog. `confirm()`
 * re-applies the change (FormField then clears the hidden fields as usual);
 * `cancel()` keeps the previous answer.
 */
export const useBranchClearGuard = ({
  form,
  formTree,
  enabled = true,
}: UseBranchClearGuardOptions) => {
  const [pending, setPending] = useState<PendingBranchClear | null>(null);
  const previousValuesRef = useRef<Record<string, FormFieldValue>>(
    form.getValues()
  );
  // Set while we re-apply a confirmed change so the watcher doesn't re-guard it
  const bypassRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;

    previousValuesRef.current = form.getValues();
    const questions = buildQuestionData(formTree);
    const gatingSlugs = new Set(
      questions
        .map(q => q.depends_on?.question_slug)
        .filter((slug): slug is string => Boolean(slug))
    );

    const subscription = form.watch((values, { name }) => {
      const currentValues = values as Record<string, FormFieldValue>;

      if (bypassRef.current || !name || !gatingSlugs.has(name)) {
        previousValuesRef.current = { ...currentValues };
        return;
      }

      const previousValues = previousValuesRef.current;
      const nextValue = currentValues[name];
      const previousValue = previousValues[name];
      if (sameValue(nextValue, previousValue)) return;

      const before = buildVisibilityMap(questions, previousValues);
      const after = buildVisibilityMap(questions, currentValues);
      // Upload/file questions are included: SubmissionAnswers::
      // CascadeClearHiddenAnswers destroys a hidden upload question's documents
      // server-side, so the user must be warned before that file is deleted.
      const affectedQuestions = questions.filter(
        q =>
          before.get(q.slug) === true &&
          after.get(q.slug) === false &&
          isAnsweredValue(previousValues[q.slug])
      );

      if (affectedQuestions.length === 0) {
        previousValuesRef.current = { ...currentValues };
        return;
      }

      // Put the old answer back until the user confirms
      form.setValue(name, previousValue, { shouldDirty: false });
      setPending({ slug: name, nextValue, affectedQuestions });
    });

    return () => subscription.unsubscribe();
  }, [form, formTree, enabled]);

  const confirm = useCallback(() => {
    if (!pending) return;
    bypassRef.current = true;
    form.setValue(pending.slug, pending.nextValue, {
      shouldDirty: true,
      shouldValidate: true,
    });
    previousValuesRef.current = { ...form.getValues() };
    bypassRef.current = false;
    setPending(null);
  }, [form, pending]);

  const cancel = useCallback(() => setPending(null), []);

  return { pending, confirm, cancel };
};
